import React from "react";
import {
  ARRIVAL_RADIUS_M,
  HOTEL_COLOR,
  PICKUP_COLOR,
  dayDate,
  distanceLabel,
  formatClock,
  formatDayClock,
  formatMinutes,
  minutesBetween,
  parseTime,
  sameDate,
} from "./tripMapModel";
import { formatDateTimeDMY12 } from "../../utils/dateFormat";

// "missed" is a place whose day is over without it being reached: "Not visited" to the office only.
const STATUS_LABELS = { visited: "Visited", here: "Here now", upcoming: "Upcoming", missed: "Not visited" };
const MISSED_COLOR = "#94a3b8";

export function BedIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 7v12M3 15h18v4M21 15v-3a3 3 0 0 0-3-3h-7v6" />
      <circle cx="7" cy="11" r="1.6" />
    </svg>
  );
}

export function StopBadge({ stop, size }) {
  const style = size ? { width: size, height: size } : undefined;
  if (stop.status === "upcoming" || stop.status === "missed") {
    const color = stop.status === "missed" ? MISSED_COLOR : stop.color;
    return <span className="trip-stop-badge is-upcoming" style={{ ...style, color }}>{stop.number}</span>;
  }
  if (stop.status === "visited") {
    return (
      <span className="trip-stop-badge" style={{ ...style, background: stop.color }}>
        <i className="bi bi-check-lg" aria-hidden="true" />
      </span>
    );
  }
  return (
    <span className="trip-stop-badge" style={{ ...style, background: stop.color, boxShadow: `0 0 0 4px ${stop.color}33` }}>
      {stop.number}
    </span>
  );
}

export function StayBadge({ size }) {
  return (
    <span className="trip-stop-badge is-stay" style={size ? { width: size, height: size } : undefined}>
      <BedIcon />
    </span>
  );
}

export function StatusChip({ status }) {
  return (
    <span className={`trip-status is-${status}`}>
      {status === "visited" ? <i className="bi bi-check-lg" aria-hidden="true" /> : null}
      {STATUS_LABELS[status]}
    </span>
  );
}

// "Reached 9:12 AM · Left 10:05 AM · 53 min" - only what has happened.
export function stopTimes(stop, now = new Date()) {
  if (stop.status === "upcoming") return "";
  const parts = [];
  if (stop.dropped) parts.push(`Reached ${formatClock(stop.dropped.capturedAtIst)}`);
  if (stop.pickedUp) {
    parts.push(`Left ${formatClock(stop.pickedUp.capturedAtIst)}`);
    const spent = minutesBetween(stop.dropped?.capturedAtIst, stop.pickedUp.capturedAtIst);
    if (spent != null) parts.push(formatMinutes(spent));
  } else if (stop.status === "here" && stop.dropped) {
    const reached = parseTime(stop.dropped.capturedAtIst);
    if (reached && now.getTime() >= reached) parts.push(`${formatMinutes(Math.round((now.getTime() - reached) / 60000))} so far`);
  }
  return parts.join(" · ");
}

// A night's stay: "Reached 3:45 PM · Back 5:15 PM", "Reached 3:45 PM · Left 2 Nov, 8:05 AM".
export function stayTimes(visit) {
  if (!visit) return "";
  const parts = [`Reached ${formatClock(visit.arrivedIst)}`];
  if (visit.backIst) {
    parts.push(`Back ${formatClock(visit.backIst)}`);
  } else if (visit.leftIst) {
    parts.push(`Left ${sameDate(visit.arrivedIst, visit.leftIst) ? formatClock(visit.leftIst) : formatDayClock(visit.leftIst)}`);
  }
  return parts.join(" · ");
}

/** Day buttons: one per trip day, plus All. The current day is marked Today. */
export function DayPills({ model, value, onChange, className = "" }) {
  return (
    <div className={`trip-day-pills ${className}`}>
      {model.days.map((day) => {
        const done = day.places.length > 0 && day.visitedCount === day.places.length;
        return (
          <button
            type="button"
            key={day.key}
            className={`trip-day-pill ${value === day.index ? "is-active" : ""}`}
            aria-pressed={value === day.index}
            onClick={() => onChange(day.index)}
          >
            <span className="trip-day-dot" style={{ background: day.color }} />
            {day.label}
            {day.index === model.currentDayIndex && !model.tripEnded ? (
              <span className="trip-day-pill-note" style={value === day.index ? undefined : { color: "#1d4ed8" }}>Today</span>
            ) : done ? (
              <i className="bi bi-check-lg trip-day-pill-note" aria-label="done" />
            ) : null}
          </button>
        );
      })}
      <button
        type="button"
        className={`trip-day-pill ${value === "all" ? "is-active" : ""}`}
        aria-pressed={value === "all"}
        onClick={() => onChange("all")}
      >
        All days
      </button>
    </div>
  );
}

export function MapLegend({ showVehicle = true, showStops = false, arrivalRadiusM = ARRIVAL_RADIUS_M }) {
  return (
    <div className="trip-legend">
      <span>
        <svg width="13" height="17" viewBox="-12 -33 24 34" aria-hidden="true"><path d="M0,0 C-2.5,-6 -11,-11.5 -11,-20.5 A11,11 0 1 1 11,-20.5 C11,-11.5 2.5,-6 0,0 Z" fill="#33445a" /><path d="M-4.6,-20.5 L-1.4,-17.3 L4.8,-23.7" fill="none" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
        Visited
      </span>
      <span>
        <svg width="13" height="17" viewBox="-12 -33 24 34" aria-hidden="true"><path d="M0,0 C-2.5,-6 -11,-11.5 -11,-20.5 A11,11 0 1 1 11,-20.5 C11,-11.5 2.5,-6 0,0 Z" fill="#33445a" /><circle cy="-20.5" r="4.5" fill="#fff" /></svg>
        Here now
      </span>
      <span>
        <svg width="13" height="17" viewBox="-12 -33 24 34" aria-hidden="true"><path d="M0,0 C-2.5,-6 -11,-11.5 -11,-20.5 A11,11 0 1 1 11,-20.5 C11,-11.5 2.5,-6 0,0 Z" fill="#fff" stroke="#33445a" strokeWidth="2.6" /></svg>
        Upcoming
      </span>
      <span>
        <span className="trip-stop-badge is-stay" style={{ width: 16, height: 16, borderRadius: 4 }}><BedIcon size={11} /></span>
        Night stay
      </span>
      <span>
        <svg width="13" height="17" viewBox="-12 -33 24 34" aria-hidden="true"><path d="M0,0 C-2.5,-6 -11,-11.5 -11,-20.5 A11,11 0 1 1 11,-20.5 C11,-11.5 2.5,-6 0,0 Z" fill={PICKUP_COLOR} /><text x="0" y="-16" textAnchor="middle" fontSize="12" fontWeight="900" fill="#fff">P</text></svg>
        Pickup
      </span>
      <span>
        <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="6.5" fill="#33445a" fillOpacity=".12" stroke="#33445a" strokeDasharray="2.5 2" strokeWidth="1.3" /></svg>
        Arrival area ({distanceLabel(arrivalRadiusM)})
      </span>
      {showVehicle ? (
        <span>
          <svg width="15" height="15" viewBox="-8 -8 16 16" aria-hidden="true"><circle r="7" fill={HOTEL_COLOR} /><path d="M0,-3.8 L3.2,3.1 L0,1.5 L-3.2,3.1 Z" fill="#fff" /></svg>
          Vehicle
        </span>
      ) : null}
      {showStops ? (
        <span>
          <span className="trip-unplanned-stop" style={{ width: 16, height: 16, fontSize: 10, borderWidth: 1 }}>
            <i className="bi bi-pause-fill" aria-hidden="true" />
          </span>
          Unplanned stop (15+ min)
        </span>
      ) : null}
    </div>
  );
}

export function MapLinkButton({ href, label }) {
  if (!href) return null;
  return (
    <a className="trip-place-link" href={href} target="_blank" rel="noopener noreferrer" aria-label={`Open ${label} in Google Maps`}>
      <i className="bi bi-box-arrow-up-right" aria-hidden="true" />
    </a>
  );
}

/**
 * One place in a day list: number and name, its status, a line of detail and a Maps button. A
 * place missed on a day that is over shows no status unless {@code showMissed} (the office).
 */
export function PlaceRow({ stop, sub, showMissed = false }) {
  const chip = stop.status !== "missed" || showMissed;
  return (
    <div className={`trip-place-row ${stop.status === "here" ? "is-here" : ""}`}>
      <StopBadge stop={stop} />
      <div className="flex-grow-1" style={{ minWidth: 0 }}>
        <div className="d-flex flex-wrap align-items-center gap-2">
          <span className="trip-place-name">{stop.number ? `${stop.number} · ` : ""}{stop.label}</span>
          {chip ? <StatusChip status={stop.status} /> : null}
        </div>
        {sub ? <div className="trip-place-sub">{sub}</div> : null}
      </div>
      <MapLinkButton href={stop.mapLink} label={stop.label} />
    </div>
  );
}

/**
 * A night's stay at the end of a day list, with the caretaker's number. {@code visit} is that
 * night's time there so far (see the model's day.stayVisit), if the vehicle has reached it.
 */
export function StayRow({ hotel, visit = null }) {
  return (
    <div className={`trip-place-row ${visit?.here ? "is-here" : ""}`}>
      <StayBadge />
      <div className="flex-grow-1" style={{ minWidth: 0 }}>
        <div className="d-flex flex-wrap align-items-center gap-2">
          <span className="trip-place-name">{hotel.name}</span>
          <span className="trip-status is-stay">{hotel.nightLabel}</span>
          {visit?.here ? <StatusChip status="here" /> : null}
          {visit && !visit.here ? (
            <span className="trip-status is-visited"><i className="bi bi-check-lg" aria-hidden="true" />Reached</span>
          ) : null}
        </div>
        {visit ? <div className="trip-place-sub">{stayTimes(visit)}</div> : null}
        {hotel.caretakerNumber ? (
          <div className="trip-place-sub">
            Caretaker {hotel.caretakerName ? `${hotel.caretakerName} · ` : ""}
            <a href={`tel:${hotel.caretakerNumber.replace(/[^\d+]/g, "")}`}>{hotel.caretakerNumber}</a>
          </div>
        ) : null}
      </div>
      <MapLinkButton href={hotel.mapLink} label={hotel.name} />
    </div>
  );
}

/**
 * The pickup at the top of Day 1: time, the customer's landmark and number, and a Maps button for
 * the exact spot. {@code action} is the customer's Set / Change button, when they may still change it.
 */
export function PickupRow({ detail, action = null }) {
  const point = detail.pickupPoint;
  const reached = detail.driverReachedPickup;
  const lines = [
    reached
      ? `Driver reached ${formatDateTimeDMY12(reached.capturedAtIst, "")}`
      : detail.vehiclePickupDateTime ? formatDateTimeDMY12(detail.vehiclePickupDateTime, "") : "",
    point?.landmark || "",
  ].filter(Boolean);
  return (
    <div className="trip-place-row">
      <span
        className={`trip-stop-badge ${reached ? "" : "is-upcoming"}`}
        style={reached ? { background: PICKUP_COLOR } : { color: PICKUP_COLOR }}
      >
        {reached ? <i className="bi bi-check-lg" aria-hidden="true" /> : "P"}
      </span>
      <div className="flex-grow-1" style={{ minWidth: 0 }}>
        <span className="trip-place-name">Pickup</span>
        {lines.map((line) => <div className="trip-place-sub" key={line}>{line}</div>)}
        {point?.contactPhone ? (
          <div className="trip-place-sub">
            Call at pickup <a href={`tel:${point.contactPhone}`}>{point.contactPhone}</a>
          </div>
        ) : null}
        {action}
      </div>
      <MapLinkButton href={detail.pickupGoogleMapLink} label="the pickup point" />
    </div>
  );
}

/**
 * One day of the trip plan, as the customer sees it under "Here now": the day's title and progress,
 * then its rows. {@code pickupAction} is the customer's Set / Change pickup button.
 */
export function TripPlanDay({ detail, model, day, now, pickupAction = null, office = false }) {
  const date = dayDate(detail.travelDate, day.index);
  return (
    <div className="trip-plan-day">
      <div className="trip-plan-head">
        <span className="trip-day-dot" style={{ background: day.color }} />
        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          <div className="trip-plan-title">{day.label}{day.title ? ` · ${day.title}` : ""}</div>
          {date ? <div className="trip-place-sub">{date}</div> : null}
        </div>
        {day.places.length ? (
          <span className="trip-plan-count">{day.visitedCount} of {day.places.length} visited</span>
        ) : null}
      </div>
      {day.places.length ? (
        <div className="trip-progress trip-plan-progress">
          <span style={{ width: `${Math.round((day.visitedCount / day.places.length) * 100)}%`, background: day.color }} />
        </div>
      ) : null}
      <TripPlanRows detail={detail} model={model} day={day} now={now} pickupAction={pickupAction} office={office} />
    </div>
  );
}

/**
 * A day's rows: pickup and the trip start open Day 1 and the trip end closes the last day.
 * {@code office} also shows places missed on a day that is over, and which places have no pin - the
 * driver marks those by hand.
 */
export function TripPlanRows({ detail, model, day, now, pickupAction = null, office = false }) {
  const first = day.index === 0;
  const last = day.index === model.days.length - 1;

  return (
    <>
      {first ? (
        <>
          <PickupRow detail={detail} action={pickupAction} />
          <MilestoneRow
            icon="bi-play-fill"
            title={detail.tripStarted ? "Trip started" : "Trip start"}
            done={Boolean(detail.tripStarted)}
            sub={detail.tripStarted ? formatDateTimeDMY12(detail.tripStarted.capturedAtIst, "") : ""}
          />
        </>
      ) : null}
      {day.places.map((stop) => (
        <PlaceRow
          key={stop.key}
          stop={stop}
          sub={stopTimes(stop, now) || (office && !stop.hasPoint ? "No map pin · the driver marks it by hand" : "")}
          showMissed={office}
        />
      ))}
      {day.tonight.map((hotel) => (
        <StayRow key={`stay-${hotel.key}`} hotel={hotel} visit={day.stayVisit?.[hotel.key]} />
      ))}
      {last ? (
        <MilestoneRow
          icon="bi-flag-fill"
          title={detail.tripEnded ? "Trip ended" : "Drop"}
          done={Boolean(detail.tripEnded)}
          sub={detail.tripEnded
            ? formatDateTimeDMY12(detail.tripEnded.capturedAtIst, "")
            : detail.vehicleDropDateTime ? `Vehicle drop ${formatDateTimeDMY12(detail.vehicleDropDateTime, "")}` : ""}
        />
      ) : null}
    </>
  );
}

function MilestoneRow({ icon, title, done, sub }) {
  return (
    <div className="trip-place-row">
      <span
        className={`trip-stop-badge ${done ? "" : "is-upcoming"}`}
        style={done ? { background: HOTEL_COLOR } : { color: HOTEL_COLOR }}
      >
        <i className={`bi ${done ? "bi-check-lg" : icon}`} aria-hidden="true" />
      </span>
      <div className="flex-grow-1" style={{ minWidth: 0 }}>
        <span className="trip-place-name">{title}</span>
        {sub ? <div className="trip-place-sub">{sub}</div> : null}
      </div>
    </div>
  );
}

/** One day's places in order, list only, ending with that night's stay. */
export function DayPlaceList({ day, now }) {
  if (!day) return null;
  return (
    <div>
      {day.places.map((stop) => (
        <PlaceRow
          key={stop.key}
          stop={stop}
          sub={stopTimes(stop, now) || (stop.hasPoint ? "" : "No map location yet")}
        />
      ))}
      {day.tonight.map((hotel) => (
        <StayRow key={`stay-${hotel.key}`} hotel={hotel} visit={day.stayVisit?.[hotel.key]} />
      ))}
    </div>
  );
}
